import axios from "axios";
import { clearLockState } from "@/utils/idleLock";

// Shared by loginApi's success path and loginMfa so the two success flows
// cannot drift: write the token, commit the profile, mark the status.
const applyLoginSuccess = (commit, data) => {
  localStorage.setItem("access_token", data.accessToken);
  // Refresh token is handled via httpOnly cookies by the backend
  commit("setUserProfile", data.user);
  commit("setLoginApiStatus", "success");
};

const moduleAuth = {
  namespaced: true,
  state: {
    loginApiStatus: "",
    userProfile: {
      uid: 0,
      firstNameTh: "",
      lastNameTh: "",
      email: "",
      mobile: ""
    },
    logOut: false,
    accessToken: null
  },
  mutations: {
    setLoginApiStatus(state, data) {
      state.loginApiStatus = data;
    },
    setUserProfile(state, data) {
      const userProfile = {
        id: data.id,
        lastName: data.lastName,
        firstName: data.firstName,
        email: data.email,
        phone: data.phone
      };
      state.userProfile = userProfile;
    },
    setLogout(state, payload) {
      state.logOut = payload;
    },
    setAccessToken(state, payload) {
      state.accessToken = payload;
    }
  },
  actions: {
    loginApi({ commit }, payload) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/api/login`, payload)
          .then(res => {
            if (res && res.data && res.data.mfaRequired) {
              // MFA challenge: write nothing to localStorage, commit nothing.
              resolve({
                outcome: "mfa_required",
                mfaToken: res.data.mfaToken,
                methods: res.data.methods
              });
              return;
            }

            if (res && res.data && res.data.mfaEnrollmentRequired) {
              // The user's clinic requires MFA and this account has none.
              // Same contract as the challenge above — this response carries no
              // accessToken, so writing localStorage here would persist the
              // string "undefined" and leave a half-authenticated state that
              // survives a reload.
              resolve({
                outcome: "mfa_enrollment_required",
                mfaToken: res.data.mfaToken,
                methods: res.data.methods
              });
              return;
            }

            if (res && res.data) {
              applyLoginSuccess(commit, res.data.data);
            }

            resolve({ outcome: "success" });
          })
          .catch(err => {
            if (err.response && err.response.status === 423) {
              // Lockout: resolve (not reject) so the view can show the
              // unlock-time message instead of falling into the generic
              // error-handling catch.
              resolve({
                outcome: "locked",
                lockedUntil:
                  err.response.data &&
                  err.response.data.data &&
                  err.response.data.data.lockedUntil
              });
              return;
            }

            commit("setLoginApiStatus", "failed");
            reject(err);
          });
      });
    },
    loginMfa({ commit }, { mfaToken, code, recoveryCode, trustDevice }) {
      return new Promise((resolve, reject) => {
        const body = { mfaToken };
        if (code !== undefined) {
          body.code = code;
        }
        if (recoveryCode !== undefined) {
          body.recoveryCode = recoveryCode;
        }
        if (trustDevice) {
          body.trustDevice = true;
        }

        axios
          .post(`/api/login/mfa`, body)
          .then(res => {
            if (res && res.data) {
              applyLoginSuccess(commit, res.data.data);
            }

            resolve({ outcome: "success" });
          })
          .catch(err => {
            // Let 401 (wrong code) / 423 (locked) reject so the view can
            // show the corresponding inline message.
            reject(err);
          });
      });
    },
    // Forced enrollment (clinic-wide MFA requirement). These two hit the
    // /api/user/mfa/enroll/* routes, which accept ONLY the short-lived
    // mfa-enroll token — not an access token, which this user does not have
    // yet. The header is set explicitly here because the global axios
    // interceptor attaches localStorage's access_token, which is absent.
    enrollTotpSetup(_, { mfaToken }) {
      return new Promise((resolve, reject) => {
        axios
          .post(
            `/api/user/mfa/enroll/totp/setup`,
            {},
            { headers: { Authorization: `Bearer ${mfaToken}` } }
          )
          .then(res => {
            resolve(res.data);
          })
          .catch(err => {
            // 401 means the token expired or the account gained a factor
            // meanwhile; the view sends the user back to the login form.
            reject(err);
          });
      });
    },
    enrollTotpActivate({ commit }, { mfaToken, code }) {
      return new Promise((resolve, reject) => {
        axios
          .post(
            `/api/user/mfa/enroll/totp/activate`,
            { code },
            { headers: { Authorization: `Bearer ${mfaToken}` } }
          )
          .then(res => {
            // This is the one enrollment call that also signs the user in, so
            // it goes through the shared success path like any other login.
            if (res && res.data && res.data.data) {
              applyLoginSuccess(commit, res.data.data);
            }

            resolve({
              outcome: "success",
              recoveryCodes: (res && res.data && res.data.recoveryCodes) || []
            });
          })
          .catch(err => {
            // 400 (wrong code) rejects so the view can show it inline and let
            // the user retype without restarting enrollment.
            reject(err);
          });
      });
    },
    passkeyLoginStart({ commit }) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/api/login/passkey/start`)
          .then(res => {
            resolve(res.data);
          })
          .catch(err => {
            // Let 401/501 reject so the view can react.
            reject(err);
          });
      });
    },
    passkeyLoginFinish({ commit }, { response, authenticationToken, rememberMe }) {
      return new Promise((resolve, reject) => {
        const body = { authenticationToken };
        if (response !== undefined) {
          body.response = response;
        }
        if (rememberMe !== undefined) {
          body.rememberMe = rememberMe;
        }

        axios
          .post(`/api/login/passkey/finish`, body)
          .then(res => {
            if (res && res.data) {
              applyLoginSuccess(commit, res.data.data);
            }

            resolve({ outcome: "success" });
          })
          .catch(err => {
            // Let 401 (failed assertion) / 501 (tenant unsupported) reject so
            // the view can show the corresponding message.
            reject(err);
          });
      });
    },
    async userLogout({ commit }) {
      // Cleared BEFORE the request, so a failing/slow logout call cannot leave
      // the lock overlay or the forced PIN dialog rendered over the login page:
      // both key off permissionContext.idleLockMinutes, which otherwise still
      // holds the previous session's clinic (spec 09 §4.3).
      commit("moduleUser/setPermissionContext", null, { root: true });
      commit("moduleUser/setPermissionContextClinicUrl", null, { root: true });
      const response = await axios
        .post(`/api/logout`)
        .catch(err => {
          console.error(err);
        });

      if (response && response.data) {
        commit("setLogout", true);
        localStorage.removeItem("access_token");
        // The idle lock flag outlives the session in localStorage, so it has
        // to go with it — otherwise the next person to log in on this machine
        // lands on a lock screen belonging to nobody (spec 09 §4.3).
        clearLockState();
        // Refresh token cookie is cleared by the backend
      } else {
        commit("setLogout", false);
      }
    },
    refreshToken({ commit }) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/api/token`)
          .then(res => {
            const { status } = res;
            if (status == 200 || status == 204) {
              // Handle different response structures
              const tokenData = res.data.data || res.data;
              if (tokenData.accessToken) {
                localStorage.setItem("access_token", tokenData.accessToken);
                commit("setAccessToken", tokenData.accessToken);
              } else {
                console.warn("No access token in refresh response");
              }
            }
            resolve(res);
          })
          .catch(err => {
            reject(err);
          });
      });
    },

    checkUserLogin(payload) {
      return new Promise((resolve, reject) => {
        const endPoint =
          payload && payload.clinicUrl && payload.branchUrl
            ? `/api/${payload.clinicUrl}/${payload.branchUrl}`
            : `/api/user/checkLogin`;

        axios
          .get(endPoint)
          .then(res => {
            resolve(res);
          })
          .catch(err => {
            reject(err);
          });
      });
    }
  },
  getters: {
    getLoginApiStatus(state) {
      return state.loginApiStatus;
    },
    getUserProfile(state) {
      return state.userProfile;
    },
    getLogout(state) {
      return state.logOut;
    },
    getAccessToken(state) {
      return state.accessToken;
    }
  }
};

export default moduleAuth;
